import { ExternalHyperlink, Paragraph, TextRun } from "docx";
import last from "lodash/last";
import random from "lodash/random";

import {
  extractProseMirrorTextByIndices,
  parseProseMirrorSchema,
} from "@/lib/proseMirror";

function removeMarks(content: any[] | null | undefined) {
  if (!content?.length) {
    return [];
  }

  const result: any[] = [];

  const joinTexts = (...texts: { type: "text"; text: string }[]) => {
    return {
      type: "text",
      text: texts.map((x) => x.text).join(""),
    };
  };

  for (let i = 0; i < content.length; i++) {
    const item = content[i];

    if (item.type === "text" && item.marks) {
      const prev = result.at(-1);
      const next = content[i + 1];

      const toJoin = [];

      if (prev?.type === "text") {
        result.pop();
        toJoin.push(prev);
      }

      toJoin.push(item);

      if (next?.type === "text" && !next.marks) {
        toJoin.push(next);
        i++;
      }

      result.push(joinTexts(...toJoin));
    } else {
      result.push(item);
    }
  }

  return result;
}

export const renderProseMirrorSchemaToGoogleDoc = (
  data: string | null | undefined,
  placeholder?: string
): Paragraph[] | null => {
  if (!data) {
    if (placeholder) {
      return [
        new Paragraph({
          children: [
            new TextRun({
              text: placeholder,
            }),
          ],
        }),
      ];
    }

    return null;
  }

  let parsedContent;

  try {
    parsedContent = JSON.parse(data)?.content || null;
  } catch (_) {
    return [
      new Paragraph({
        children: [
          new TextRun({
            text: data,
          }),
        ],
      }),
    ];
  }

  const result: any[] = [];

  function renderLevel(
    content: any[] | null | undefined,
    level = -1,
    nestedLists: [string, number][] = []
  ) {
    if (!content?.length) {
      return;
    }

    removeMarks(content).forEach((item) => {
      if (item.type === "text") {
        const linkData = item.marks?.find((x: any) => x.type === "link");

        const content = linkData
          ? new ExternalHyperlink({
              children: [
                new TextRun({
                  text: item.text,
                  style: "Hyperlink",
                }),
              ],
              link: linkData.attrs.href,
            })
          : new TextRun({
              text: item.text,
            });

        const [lastList, listInstance] = last(nestedLists) || [];

        if (lastList === "bulletList") {
          return result.push(
            new Paragraph({
              bullet: {
                level: Math.min(level, 8),
              },
              children: [content],
            })
          );
        }

        if (lastList === "orderedList") {
          return result.push(
            new Paragraph({
              numbering: {
                level: Math.min(level, 8),
                instance: listInstance,
                reference: "simpleNumbering",
                custom: true,
              },
              children: [content],
            })
          );
        }

        result.push(
          new Paragraph({
            children: [content],
          })
        );
      }

      if (item.type === "paragraph") {
        if (!item.content?.length) {
          return result.push(new Paragraph({}));
        }

        renderLevel(item.content, level, nestedLists);
      }

      if (item.type === "bulletList") {
        renderLevel(item.content, level + 1, [
          ...nestedLists,
          ["bulletList", random(1, 1000000)],
        ]);
      }

      if (item.type === "orderedList") {
        renderLevel(item.content, level + 1, [
          ...nestedLists,
          ["orderedList", random(1, 1000000)],
        ]);
      }

      if (item.type === "listItem") {
        renderLevel(item.content, level, nestedLists);
      }
    }, []);
  }

  if (!parsedContent?.length) {
    if (placeholder) {
      return [
        new Paragraph({
          children: [
            new TextRun({
              text: placeholder,
            }),
          ],
        }),
      ];
    }

    return null;
  }

  renderLevel(parsedContent);

  return result;
};

/**
 * Renders ProseMirror schema with highlighted fragments
 * @param data - ProseMirror JSON string
 * @param highlights - Array of fragments to highlight: { from: number; to: number; colorHex: string }
 * @param placeholder - Placeholder text if data is empty
 * @param onFragmentEnd - Callback called when a highlighted fragment ends, receives (from, to) and should return Paragraph[] to insert
 */
export const renderProseMirrorSchemaToGoogleDocWithHighlights = (
  data: string | null | undefined,
  highlights: Array<{ from: number; to: number; colorHex?: string }> = [],
  placeholder?: string,
  onFragmentEnd?: (from: number, to: number) => Paragraph[]
): Paragraph[] | null => {
  if (!data) {
    if (placeholder) {
      return [
        new Paragraph({
          children: [
            new TextRun({
              text: placeholder,
            }),
          ],
        }),
      ];
    }
    return null;
  }

  let parsedContent;
  try {
    parsedContent = JSON.parse(data)?.content || null;
  } catch (_) {
    return [
      new Paragraph({
        children: [
          new TextRun({
            text: data,
          }),
        ],
      }),
    ];
  }

  const result: any[] = [];
  let currentPos = 0;
  const insertedComments = new Set<string>(); // Track which highlights already had comments inserted
  // Вычисляем общую длину текста для проверки последнего фрагмента
  const totalTextLength = parseProseMirrorSchema(data).length;

  // Sort highlights by position
  const sortedHighlights = [...highlights].sort((a, b) => a.from - b.from);
  // Логируем highlights, которые пришли в функцию рендеринга
  console.log("[proseMirror] Highlights received:", sortedHighlights);
  // Проверяем, правильно ли мы понимаем позиции, извлекая текст по индексам
  for (const h of sortedHighlights) {
    const sectionFrom = h.from;
    const sectionTo = h.to - 1; // to - 1 потому что to эксклюзивный
    const expectedText = extractProseMirrorTextByIndices(
      data,
      sectionFrom,
      sectionTo + 1
    );
    console.log("[proseMirror] Expected text for highlight:", {
      from: sectionFrom,
      to: sectionTo,
      expectedText,
      highlight: h,
    });
  }

  // Helper function to check if current position is in a highlight
  // highlight.from = sectionFrom (включительный индекс символа)
  // highlight.to = sectionTo + 1 (эксклюзивный, потому что sectionTo включительный)
  // pos - это позиция символа в plain text (0-based индекс)
  // Например, если sectionFrom=0 и sectionTo=4, то выделяются символы с индексами 0,1,2,3,4
  // highlight.from = 0, highlight.to = 5
  // Позиция pos находится в highlight, если: pos >= highlight.from && pos < highlight.to
  const getHighlightForPosition = (
    pos: number
  ): {
    colorHex?: string;
  } | null => {
    for (const highlight of sortedHighlights) {
      // highlight.from это sectionFrom (включительный индекс символа)
      // highlight.to это sectionTo + 1 (эксклюзивный конец)
      // Позиция pos находится в highlight, если: pos >= highlight.from && pos < highlight.to
      if (pos >= highlight.from && pos < highlight.to) {
        return { colorHex: highlight.colorHex };
      }
    }
    return null;
  };

  function renderLevel(
    content: any[] | null | undefined,
    level = -1,
    nestedLists: [string, number][] = []
  ) {
    if (!content?.length) {
      return;
    }

    removeMarks(content).forEach((item) => {
      if (item.type === "text") {
        const linkData = item.marks?.find((x: any) => x.type === "link");
        const text = item.text || "";

        /**
         * Строгое разбиение текстового узла на сегменты по границам sectionFrom/sectionTo.
         *
         * Правила:
         * - Берём диапазон узла [nodeStart, nodeEnd) в plain text координатах.
         * - Собираем все точки разреза:
         *   - nodeStart, nodeEnd;
         *   - все highlight.from и highlight.to, которые попадают внутрь узла.
         * - Сортируем, убираем дубликаты, получаем точки p0 < p1 < ... < pn.
         * - Каждый сегмент = [pi, pi+1). Сегменты НЕ пересекаются и полностью покрывают узел.
         * - Сегмент считается подсвеченным, если он целиком лежит внутри какого‑то highlight
         *   (from <= startPos && endPos <= to).
         */
        const nodeStart = currentPos;
        const nodeEnd = currentPos + text.length;

        const cutPoints: number[] = [nodeStart, nodeEnd];

        for (const h of sortedHighlights) {
          const hStart = h.from;
          const hEnd = h.to; // эксклюзивный конец

          // highlight не пересекается с узлом
          if (hEnd <= nodeStart || hStart >= nodeEnd) continue;

          // внутренняя часть highlight, попадающая в узел
          const clampedStart = Math.max(nodeStart, hStart);
          const clampedEnd = Math.min(nodeEnd, hEnd);

          cutPoints.push(clampedStart, clampedEnd);
        }

        // Упорядочиваем и убираем дубликаты
        const uniquePoints = Array.from(new Set(cutPoints)).sort(
          (a, b) => a - b
        );

        const textSegments: Array<{
          text: string;
          startPos: number;
          endPos: number;
        }> = [];

        for (let i = 0; i < uniquePoints.length - 1; i++) {
          const segStartAbs = uniquePoints[i];
          const segEndAbs = uniquePoints[i + 1];

          if (segEndAbs <= segStartAbs) continue;

          const segText = text.substring(
            segStartAbs - nodeStart,
            segEndAbs - nodeStart
          );

          textSegments.push({
            text: segText,
            startPos: segStartAbs,
            endPos: segEndAbs,
          });
        }

        // Если нет сегментов (текст пустой), добавляем один пустой сегмент
        if (textSegments.length === 0 && text.length > 0) {
          textSegments.push({
            text,
            startPos: nodeStart,
            endPos: nodeEnd,
          });
        }

        // Render each segment and insert comments immediately after highlighted fragments
        const [lastList, listInstance] = last(nestedLists) || [];

        // Разбиваем сегменты на группы: до фрагмента, фрагмент, после фрагмента
        for (let segIndex = 0; segIndex < textSegments.length; segIndex++) {
          const segment = textSegments[segIndex];

          // Находим highlight, который полностью покрывает этот сегмент
          const segmentHighlight = sortedHighlights.find(
            (h) => segment.startPos >= h.from && segment.endPos <= h.to
          );
          const isHighlighted = !!segmentHighlight;

          // Проверяем, является ли этот сегмент началом фрагмента (для открывающей кавычки)
          let isFragmentStart = false;
          let isFragmentEnd = false;
          if (isHighlighted && segmentHighlight) {
            // Сегмент является началом фрагмента, если его начало совпадает с началом highlight
            isFragmentStart = segment.startPos === segmentHighlight.from;
            // Сегмент является концом фрагмента, если его конец совпадает с концом highlight
            isFragmentEnd = segment.endPos === segmentHighlight.to;
          }

          // Оборачиваем текст в кавычки: открывающая в начале фрагмента, закрывающая в конце
          let segmentText = segment.text;
          if (isHighlighted) {
            if (isFragmentStart && isFragmentEnd) {
              // Весь фрагмент в одном сегменте
              segmentText = `«${segment.text}»`;
            } else if (isFragmentStart) {
              // Начало фрагмента - открывающая кавычка
              segmentText = `«${segment.text}`;
            } else if (isFragmentEnd) {
              // Конец фрагмента - закрывающая кавычка
              segmentText = `${segment.text}»`;
            }
            // Середина фрагмента остается без кавычек (они уже открыты в начале)
          }

          // Create text run for this segment
          const segmentRun = linkData
            ? new ExternalHyperlink({
                children: [
                  new TextRun({
                    text: segmentText,
                    style: "Hyperlink",
                    italics: isHighlighted,
                    // Используем highlight, а не color, чтобы фон текста в DOCX / Google Docs
                    // совпадал с цветом комментария
                    highlight: segmentHighlight?.colorHex,
                  }),
                ],
                link: linkData.attrs.href,
              })
            : new TextRun({
                text: segmentText,
                italics: isHighlighted,
                // Используем highlight, а не color, чтобы фон текста подсвечивался цветом комментария
                highlight: segmentHighlight?.colorHex,
              });

          // Проверяем, содержит ли этот сегмент конец какого-либо фрагмента
          // Просто используем sectionFrom и sectionTo из highlights
          let fragmentEndPos: number | null = null;
          let fragmentStartPos: number | null = null;
          const commentsToInsert: Paragraph[] = [];
          if (onFragmentEnd && isHighlighted) {
            for (const h of sortedHighlights) {
              const highlightKey = `${h.from}_${h.to}`;
              // h.from = sectionFrom (включительный индекс символа)
              // h.to = sectionTo + 1 (эксклюзивный конец)
              // sectionTo = h.to - 1 (включительный конец)
              const sectionFrom = h.from;
              const sectionTo = h.to - 1;
              // Логируем проверку фрагмента
              console.log("[proseMirror] Checking fragment:", {
                highlightKey,
                sectionFrom,
                sectionTo,
                segmentStartPos: segment.startPos,
                segmentEndPos: segment.endPos,
                segmentText: segment.text.substring(0, 20),
              });
              // Проверяем, содержит ли сегмент конец фрагмента
              if (
                segment.endPos > sectionTo &&
                segment.startPos <= sectionTo &&
                !insertedComments.has(highlightKey)
              ) {
                fragmentEndPos = sectionTo;
                fragmentStartPos = sectionFrom;
                // Логируем найденный фрагмент
                console.log("[proseMirror] Found fragment in segment:", {
                  fragmentStartPos,
                  fragmentEndPos,
                  segmentStartPos: segment.startPos,
                  segmentEndPos: segment.endPos,
                  fragmentStartInSegment: fragmentStartPos - segment.startPos,
                  fragmentEndInSegment: fragmentEndPos - segment.startPos + 1,
                });
                // h.to это to+1 (эксклюзивный конец), передаем h.to для поиска в map
                const commentParagraphs = onFragmentEnd(h.from, h.to);
                if (commentParagraphs.length > 0) {
                  commentsToInsert.push(...commentParagraphs);
                  insertedComments.add(highlightKey);
                }
                break; // Берем первый найденный фрагмент
              }
            }
          }

          // Если этот сегмент содержит конец фрагмента, разбиваем его на две части
          // fragmentEndPos = sectionTo, fragmentStartPos = sectionFrom
          // Проверяем, что фрагмент пересекается с сегментом
          if (
            fragmentEndPos !== null &&
            fragmentStartPos !== null &&
            fragmentStartPos < segment.endPos &&
            fragmentEndPos >= segment.startPos &&
            fragmentEndPos < segment.endPos
          ) {
            // Первая часть: выделенный фрагмент [sectionFrom, sectionTo]
            // sectionFrom и sectionTo включительны (0-based индексы)
            // fragmentStartPos = sectionFrom, fragmentEndPos = sectionTo
            // segment.startPos - это позиция начала сегмента в общем тексте
            // segment.text - это текст сегмента, начинающийся с позиции 0
            // Нужно выделить текст от sectionFrom до sectionTo включительно
            // fragmentStartInSegment = позиция начала фрагмента относительно начала сегмента
            const fragmentStartInSegment = Math.max(
              0,
              fragmentStartPos - segment.startPos
            );
            // sectionTo включительный, для substring нужен sectionTo + 1 (эксклюзивный)
            // fragmentEndInSegment = позиция конца фрагмента (эксклюзивная) относительно начала сегмента
            const fragmentEndInSegment = fragmentEndPos - segment.startPos + 1;
            // Выделяем только текст в диапазоне [fragmentStartInSegment, fragmentEndInSegment)
            const fragmentText = segment.text.substring(
              Math.max(0, fragmentStartInSegment),
              Math.min(segment.text.length, fragmentEndInSegment)
            );
            // Логируем выделение фрагмента
            console.log("[proseMirror] Extracting fragment text:", {
              fragmentStartPos,
              fragmentEndPos,
              sectionFrom: fragmentStartPos,
              sectionTo: fragmentEndPos,
              segmentStartPos: segment.startPos,
              segmentEndPos: segment.endPos,
              segmentTextLength: segment.text.length,
              fragmentStartInSegment,
              fragmentEndInSegment,
              extractedText: fragmentText,
              fullSegmentText: segment.text,
            });
            // Оборачиваем фрагмент в русские ёлочки «...»
            const fragmentTextWithQuotes = `«${fragmentText}»`;

            const fragmentRun = linkData
              ? new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: fragmentTextWithQuotes,
                      style: "Hyperlink",
                      italics: true,
                      color: getHighlightForPosition(segment.startPos)
                        ?.colorHex,
                    }),
                  ],
                  link: linkData.attrs.href,
                })
              : new TextRun({
                  text: fragmentTextWithQuotes,
                  italics: true,
                  color: getHighlightForPosition(segment.startPos)?.colorHex,
                });

            // Создаем параграф для выделенного фрагмента
            let fragmentParagraph: Paragraph;
            if (lastList === "bulletList") {
              fragmentParagraph = new Paragraph({
                bullet: {
                  level: Math.min(level, 8),
                },
                children: [fragmentRun],
              });
            } else if (lastList === "orderedList") {
              fragmentParagraph = new Paragraph({
                numbering: {
                  level: Math.min(level, 8),
                  instance: listInstance,
                  reference: "simpleNumbering",
                  custom: true,
                },
                children: [fragmentRun],
              });
            } else {
              fragmentParagraph = new Paragraph({
                children: [fragmentRun],
              });
            }

            result.push(fragmentParagraph);
            // Пустая строка после фрагмента, перед комментариями
            result.push(new Paragraph({}));
            // Вставляем комментарии после пустой строки
            result.push(...commentsToInsert);
            // Пустая строка после комментариев, перед продолжением текста
            result.push(new Paragraph({}));

            // Вторая часть: после фрагмента (обычный текст)
            const afterFragmentStart = fragmentEndPos - segment.startPos + 1;
            const afterFragmentText =
              afterFragmentStart < segment.text.length
                ? segment.text.substring(afterFragmentStart)
                : "";
            if (afterFragmentText.length > 0) {
              const afterFragmentRun = linkData
                ? new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: afterFragmentText,
                        style: "Hyperlink",
                        italics: false,
                      }),
                    ],
                    link: linkData.attrs.href,
                  })
                : new TextRun({
                    text: afterFragmentText,
                    italics: false,
                  });

              // Добавляем к следующему параграфу или создаем новый
              // Пока что создаем отдельный параграф
              let afterParagraph: Paragraph;
              if (lastList === "bulletList") {
                afterParagraph = new Paragraph({
                  bullet: {
                    level: Math.min(level, 8),
                  },
                  children: [afterFragmentRun],
                });
              } else if (lastList === "orderedList") {
                afterParagraph = new Paragraph({
                  numbering: {
                    level: Math.min(level, 8),
                    instance: listInstance,
                    reference: "simpleNumbering",
                    custom: true,
                  },
                  children: [afterFragmentRun],
                });
              } else {
                afterParagraph = new Paragraph({
                  children: [afterFragmentRun],
                });
              }

              result.push(afterParagraph);
            }
            continue; // Пропускаем обычную обработку этого сегмента
          }

          // Если этот сегмент заканчивает фрагмент точно на границе, создаем отдельный параграф
          // и вставляем комментарии сразу после него
          if (commentsToInsert.length > 0) {
            // Создаем параграф только для этого сегмента (выделенный фрагмент)
            let segmentParagraph: Paragraph;
            if (lastList === "bulletList") {
              segmentParagraph = new Paragraph({
                bullet: {
                  level: Math.min(level, 8),
                },
                children: [segmentRun],
              });
            } else if (lastList === "orderedList") {
              segmentParagraph = new Paragraph({
                numbering: {
                  level: Math.min(level, 8),
                  instance: listInstance,
                  reference: "simpleNumbering",
                  custom: true,
                },
                children: [segmentRun],
              });
            } else {
              segmentParagraph = new Paragraph({
                children: [segmentRun],
              });
            }

            result.push(segmentParagraph);
            // Пустая строка после фрагмента, перед комментариями
            result.push(new Paragraph({}));
            // Вставляем комментарии после пустой строки
            result.push(...commentsToInsert);
            // Пустая строка после комментариев, перед продолжением текста
            result.push(new Paragraph({}));
          } else {
            // Обычный сегмент - добавляем к текущему параграфу
            // Собираем все последовательные сегменты без комментариев в один параграф
            const currentRuns: any[] = [segmentRun];
            let nextSegIndex = segIndex + 1;

            // Собираем следующие сегменты, которые не заканчивают фрагменты
            while (nextSegIndex < textSegments.length) {
              const nextSegment = textSegments[nextSegIndex];
              const nextHighlight = getHighlightForPosition(
                nextSegment.startPos
              );
              const nextIsHighlighted = nextHighlight !== null;

              // Проверяем, заканчивает ли следующий сегмент фрагмент
              let nextEndsHighlight = false;
              if (onFragmentEnd && nextIsHighlighted) {
                for (const h of sortedHighlights) {
                  const highlightKey = `${h.from}_${h.to}`;
                  if (
                    nextSegment.endPos >= h.to &&
                    nextSegment.startPos < h.to &&
                    !insertedComments.has(highlightKey)
                  ) {
                    nextEndsHighlight = true;
                    break;
                  }
                }
              }

              if (nextEndsHighlight) break;

              const nextRun = linkData
                ? new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: nextSegment.text,
                        style: "Hyperlink",
                        italics: nextIsHighlighted,
                        color: nextHighlight?.colorHex,
                      }),
                    ],
                    link: linkData.attrs.href,
                  })
                : new TextRun({
                    text: nextSegment.text,
                    italics: nextIsHighlighted,
                    color: nextHighlight?.colorHex,
                  });

              currentRuns.push(nextRun);
              nextSegIndex++;
            }

            // Создаем параграф с собранными runs
            let paragraph: Paragraph;
            if (lastList === "bulletList") {
              paragraph = new Paragraph({
                bullet: {
                  level: Math.min(level, 8),
                },
                children: currentRuns,
              });
            } else if (lastList === "orderedList") {
              paragraph = new Paragraph({
                numbering: {
                  level: Math.min(level, 8),
                  instance: listInstance,
                  reference: "simpleNumbering",
                  custom: true,
                },
                children: currentRuns,
              });
            } else {
              paragraph = new Paragraph({
                children: currentRuns,
              });
            }

            result.push(paragraph);
            segIndex = nextSegIndex - 1; // -1 потому что цикл увеличит на 1
          }
        }

        const textEndPos = currentPos + text.length;

        // Also check for any highlights that ended at the text end position
        // Это важно для последнего фрагмента, который может заканчиваться в конце текста
        if (onFragmentEnd) {
          for (const h of sortedHighlights) {
            const highlightKey = `${h.from}_${h.to}`;
            // h.to это to+1 (эксклюзивный конец), проверяем h.to - 1 (включительный конец)
            // Фрагмент находится в этом текстовом узле, если:
            // - начало фрагмента (h.from) находится в диапазоне [currentPos, textEndPos]
            // - или конец фрагмента (h.to - 1) находится в диапазоне [currentPos, textEndPos]
            // - или фрагмент полностью содержит этот текстовый узел
            const fragmentStart = h.from;
            const fragmentEnd = h.to - 1; // включительный конец
            const fragmentInThisNode =
              (fragmentStart >= currentPos && fragmentStart < textEndPos) ||
              (fragmentEnd >= currentPos && fragmentEnd < textEndPos) ||
              (fragmentStart <= currentPos && fragmentEnd >= textEndPos);

            if (
              fragmentInThisNode &&
              !insertedComments.has(highlightKey) &&
              textEndPos >= fragmentEnd
            ) {
              // h.to это to+1 (эксклюзивный конец), передаем h.to для поиска в map
              const commentParagraphs = onFragmentEnd(h.from, h.to);
              if (commentParagraphs.length > 0) {
                result.push(...commentParagraphs);
                insertedComments.add(highlightKey);
              }
            }
          }
        }

        currentPos = textEndPos;
      }

      if (item.type === "paragraph") {
        if (!item.content?.length) {
          return result.push(new Paragraph({}));
        }
        renderLevel(item.content, level, nestedLists);
      }

      if (item.type === "bulletList") {
        renderLevel(item.content, level + 1, [
          ...nestedLists,
          ["bulletList", random(1, 1000000)],
        ]);
      }

      if (item.type === "orderedList") {
        renderLevel(item.content, level + 1, [
          ...nestedLists,
          ["orderedList", random(1, 1000000)],
        ]);
      }

      if (item.type === "listItem") {
        renderLevel(item.content, level, nestedLists);
      }
    });
  }

  if (!parsedContent?.length) {
    if (placeholder) {
      return [
        new Paragraph({
          children: [
            new TextRun({
              text: placeholder,
            }),
          ],
        }),
      ];
    }

    return null;
  }

  renderLevel(parsedContent);

  // Финальная проверка: если какой-то фрагмент не был обработан (особенно последний),
  // обрабатываем его вручную
  if (onFragmentEnd) {
    for (const h of sortedHighlights) {
      const highlightKey = `${h.from}_${h.to}`;
      if (!insertedComments.has(highlightKey)) {
        // Проверяем, что фрагмент находится в тексте
        if (h.from < totalTextLength && h.to - 1 < totalTextLength) {
          const commentParagraphs = onFragmentEnd(h.from, h.to);
          if (commentParagraphs.length > 0) {
            // Вставляем комментарии в конец результата, но перед общими комментариями
            result.push(...commentParagraphs);
            insertedComments.add(highlightKey);
          }
        }
      }
    }
  }

  return result;
};
